import { useCallback, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import type { ProductsSequence } from 'common/contracts';
import { EFeatureFlag } from 'constants/enums';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'components/ui/dialog';

import { useProgressBarCheats } from '../hooks/useProgressBarCheats';

interface IProgressBarCheatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: string;
  productsSequence: ProductsSequence[];
}

export const ProgressBarCheatsModal = ({
  isOpen,
  onClose,
  offerId,
  productsSequence
}: IProgressBarCheatsModalProps): React.ReactElement | null => {
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const hasFeatureFlagCheats =
    !!featureFlags?.[EFeatureFlag.DASHBOARD_PROGRESS_BAR_CHEATS];

  const [playerId, setPlayerId] = useState<string>('');
  const [mission, setMission] = useState<string>('');
  const [grantPoints, setGrantPoints] = useState<string>('');

  const handleClose = useCallback((): void => {
    setPlayerId('');
    setMission('');
    setGrantPoints('');
    onClose();
  }, [onClose]);

  const { submitCheats } = useProgressBarCheats({ onSuccess: handleClose });

  const missionOptions = useMemo(
    () =>
      productsSequence.map((seq) => ({
        value: String(seq.index),
        label: `Mission ${seq.index}`
      })),
    [productsSequence]
  );

  const missionMaxPoints = useMemo((): number => {
    const selectedIndex = Number(mission);
    if (!mission) return 0;
    return productsSequence
      .filter((seq) => seq.index >= selectedIndex)
      .reduce((sum, seq) => sum + (seq.barSize ?? 0), 0);
  }, [productsSequence, mission]);

  const handlePlayerIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setPlayerId(e.target.value);
    },
    []
  );

  const handleMissionChange = useCallback((value: string): void => {
    setMission(value);
  }, []);

  const handleGrantPointsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setGrantPoints(e.target.value);
    },
    []
  );

  const isSubmitDisabled = useMemo(
    (): boolean =>
      !playerId ||
      !mission ||
      !grantPoints ||
      Number(grantPoints) > missionMaxPoints ||
      Number(grantPoints) <= 0,
    [playerId, mission, grantPoints, missionMaxPoints]
  );

  const handleSubmit = useCallback((): void => {
    submitCheats.mutate({
      playerId,
      offerId,
      selectedMissionIndex: Number(mission) ,
      collectedPoints: Number(grantPoints)
    });
  }, [submitCheats, offerId, playerId, mission, grantPoints]);

  if (!hasFeatureFlagCheats) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="bg-white p-0 max-w-[440px] rounded-lg"
        showCloseButton
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-lg font-semibold">Cheats</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 px-6 py-4">
          <Input
            label="Player ID"
            placeholder="Player ID"
            value={playerId}
            onChange={handlePlayerIdChange}
            required
          />

          <InputSelectDropdown
            label="Set Mission"
            options={missionOptions}
            placeholder="Select"
            onOptionChange={handleMissionChange}
          />

          <Input
            label="Grant Points"
            type="number"
            placeholder={`Max : ${missionMaxPoints}`}
            max={missionMaxPoints}
            value={grantPoints}
            onChange={handleGrantPointsChange}
          />
        </div>

        <DialogFooter className="px-6 pb-6 pt-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            className="bg-base-foreground hover:bg-base-foreground/90 text-white disabled:opacity-50"
            disabled={isSubmitDisabled}
          >
            Set Cheats
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
